<template>
  <div class="role-field">
    <Checkbox
      @change="onCheckboxChange"
      :disabled="disabled"
      :selected-values="selectedValues"
      :options="[role]"
      isLarge
      isColumn
      class="checkbox--with-organisation-unit"
    />
    <div
      class="role-field__organisation-unit"
      v-if="showOrganisationSelect && !role.hasOnlyRootOrganisation"
    >
      <v-autocomplete
        :search-input.sync="searchOrganisations"
        placeholder="Please enter at least 3 characters"
        :disabled="disabled"
        label="Organisation Unit"
        v-if="showOrganisationSelect && !role.hasOnlyRootOrganisation"
        :errorMessages="organisationSelectError"
        :loading="isLoadingOrganisations"
        :value="selectedOrganisations"
        hide-no-data
        @input="handleSelectedOrganisation"
        :items="formattedOrganisations"
        item-text="label"
        return-object
        ref="autocomplete"
      />
      <div class="d-flex flex-column">
        <div
          v-for="(organisation, index) in selectedOrganisations"
          :key="index"
        >
          <v-chip
            close
            @click:close="unselectOrganisation(organisation)"
            class="selected-worker-chip mr-2"
          >
            <div>
              {{ organisation.name }}
              <span v-if="organisation.costCode">
                {{ `(${organisation.costCode})` }}
              </span>
            </div>
          </v-chip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from "@/components/common/Checkbox";
import { filter, size, map, find } from "lodash";
import { FETCH_ORGANISATIONS } from "@/store/modules/organisations/actions";
import { SET_ORGANISATIONS } from "@/store/modules/organisations/mutations";
import { createNamespacedHelpers } from "vuex";
import { ORGANISATIONS_NAMESPACE } from "@/store/modules/organisations";
import {
  GET_IS_FETCHING_ORGANISATIONS,
  GET_ORGANISATIONS
} from "@/store/modules/organisations/getters";

const {
  mapActions: mapOrganisationActions,
  mapGetters: mapOrganisationGetters,
  mapMutations: mapOrganisationMutations
} = createNamespacedHelpers(ORGANISATIONS_NAMESPACE);

export default {
  name: "RoleField",
  components: { Checkbox },
  props: {
    isSelected: Boolean,
    disabled: Boolean,
    role: {
      type: Object,
      required: true
    },
    initialSelectedOrganisations: {
      type: Array,
      required: false
    },
    rootOrganisation: Object,
    showValidationErrors: Boolean
  },
  created() {
    this.setOrganisations([]);
    this.adjustRoleOrganisations();
  },
  data() {
    return {
      showOrganisationSelect: false,
      selectedValues: this.isSelected ? [this.role.value] : [],
      selectedOrganisations: [],
      searchOrganisations: null
    };
  },
  computed: {
    organisationSelectError() {
      const showError =
        this.showValidationErrors && !this.selectedOrganisations.length;
      return showError ? "This field is required" : "";
    },
    ...mapOrganisationGetters({
      organisations: GET_ORGANISATIONS,
      isLoadingOrganisations: GET_IS_FETCHING_ORGANISATIONS
    }),
    formattedOrganisations() {
      return map(this.organisations, organisation => {
        const costCode = organisation.costCode
          ? `(${organisation.costCode})`
          : "";
        return {
          ...organisation,
          label: `${organisation.name}${costCode}`
        };
      });
    }
  },
  methods: {
    ...mapOrganisationMutations({
      setOrganisations: SET_ORGANISATIONS
    }),
    ...mapOrganisationActions({
      fetchOrganisations: FETCH_ORGANISATIONS
    }),
    adjustRoleOrganisations() {
      if (this.role.hasOnlyRootOrganisation) {
        this.selectedOrganisations = this.rootOrganisation;
      } else if (size(this.initialSelectedOrganisations)) {
        this.showOrganisationSelect = true;
        this.selectedOrganisations = [...this.initialSelectedOrganisations];
      }
    },
    addRole() {
      this.$emit("addRole", {
        name: this.role.value,
        organisations: this.selectedOrganisations
      });
    },
    removeRole() {
      this.$emit("removeRole", this.role.value);
    },
    updateRole() {
      this.$emit("updateRole", {
        name: this.role.value,
        organisations: this.selectedOrganisations
      });
    },
    onCheckboxChange(val) {
      const checkboxValue = Boolean(size(val));
      this.showOrganisationSelect = checkboxValue;
      if (!checkboxValue) {
        this.selectedOrganisations = [];
        this.removeRole();
      } else {
        this.addRole();
      }
    },
    handleSelectedOrganisation(selectedOrganisation) {
      if (
        !find(this.selectedOrganisations, {
          id: selectedOrganisation.id
        })
      ) {
        this.selectedOrganisations = [
          ...this.selectedOrganisations,
          selectedOrganisation
        ];
      }
      this.$nextTick(() => {
        this.$refs.autocomplete.isMenuActive = false;
      });
      this.updateRole();
    },
    unselectOrganisation(organisationToUnselect) {
      this.selectedOrganisations = filter(
        this.selectedOrganisations,
        ({ id }) => id !== organisationToUnselect.id
      );
      this.updateRole();
    },
    fetchOrganisationsDebounced(val) {
      if (size(val) >= 3) {
        clearTimeout(this._timerId);
        this._timerId = setTimeout(() => {
          this.fetchOrganisations({
            filter: {
              name: val
            }
          });
        }, 500);
      }
    }
  },
  watch: {
    searchOrganisations(val) {
      this.fetchOrganisationsDebounced(val);
    }
  }
};
</script>

<style lang="scss">
.role-field {
  margin-bottom: 20px;

  .checkbox.checkbox--large {
    margin: 0;
  }

  &__organisation-unit {
    box-shadow: $box-shadow;
    background-color: $white;
    border: 2px solid $secondary-line;
    border-top: 0px;
    padding: 15px;
    border-radius: 0px 0px 4px 4px;
  }
}

.checkbox--with-organisation-unit {
  .checkbox-wrapper {
    margin-bottom: 0px;
    padding-bottom: 0px;
  }

  .checkbox--large__active {
    border-radius: 4px 4px 0px 0px;
  }
}
</style>
